import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CategoryDropdown from '@/components/base/CategoryDropdown';

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(!isHome);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setScrolled(!isHome);
  }, [isHome]);

  useEffect(() => {
    if (!isHome) return;
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const isLight = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className={`font-heading text-lg md:text-xl font-semibold transition-colors duration-300 ${
                isLight ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              litbuy spreadsheets
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 cursor-pointer ${
                  isLight
                    ? 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 cursor-pointer inline-flex items-center gap-1 ${
                  isLight
                    ? 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Shopping Guide
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-external-link-line text-[10px]"></i>
                </span>
              </a>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <CategoryDropdown isLight={isLight} />
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`whitespace-nowrap hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer ${
                isLight
                  ? 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              litbuy spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`whitespace-nowrap inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer ${
                isLight
                  ? 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 cursor-pointer ${
              isLight
                ? 'text-background-50 hover:bg-background-50/10'
                : 'text-foreground-950 hover:bg-background-100'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-3">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 transition-colors duration-150 cursor-pointer hover:bg-background-100"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-home-4-line"></i>
            </span>
            Home
          </Link>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 transition-colors duration-150 cursor-pointer hover:bg-background-100"
          >
            <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
              <i className="ri-shopping-bag-3-line"></i>
            </span>
            Shopping Guide
          </a>
          <div className="px-4 py-3">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Shop Categories</p>
            <CategoryDropdown isLight={false} isMobile />
          </div>
          <div className="flex gap-3 px-4 pt-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap flex-1 text-center rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-all duration-200 cursor-pointer hover:bg-primary-600"
            >
              litbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap flex-1 text-center rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-all duration-200 cursor-pointer hover:bg-primary-600"
            >
              buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}